import { DataExport, ImportResult, ApiProvider, ChatSession, ChatTemplate } from '@/types';

/**
 * 数据导入工具类
 * 提供对话数据的验证、清理和导入功能
 */
export class DataImportUtil {
  /**
   * 验证导入数据
   */
  static validateImportData(data: any): { valid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // 基本验证
    if (!data || typeof data !== 'object') {
      errors.push('导入数据不是有效的 JSON 对象');
      return { valid: false, errors, warnings };
    }
    
    // 版本验证
    if (!data.version || typeof data.version !== 'string') {
      errors.push('缺少数据版本信息');
    } else if (data.version !== '1.0.0') {
      warnings.push(`数据版本 ${data.version} 可能与当前系统版本不兼容`);
    }
    
    // 导出时间验证
    if (!data.exportedAt || typeof data.exportedAt !== 'string') {
      warnings.push('缺少导出时间信息');
    } else {
      try {
        const exportDate = new Date(data.exportedAt);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - exportDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diffDays > 365) {
          warnings.push(`数据导出时间超过一年（${diffDays} 天前），可能存在兼容性问题`);
        }
      } catch {
        warnings.push('导出时间格式无效');
      }
    }
    
    // 数据结构验证
    if (data.providers && !Array.isArray(data.providers)) {
      errors.push('供应商数据格式无效');
    }
    
    if (data.sessions && !Array.isArray(data.sessions)) {
      errors.push('会话数据格式无效');
    }
    
    if (data.templates && !Array.isArray(data.templates)) {
      errors.push('模板数据格式无效');
    }
    
    // 如果没有数据
    if (!data.providers && !data.sessions && !data.templates) {
      warnings.push('导入数据为空，没有可导入的内容');
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }
  
  /**
   * 清理导入数据
   */
  static cleanImportData(data: DataExport): DataExport {
    const cleanedData: DataExport = {
      version: data.version || '1.0.0',
      exportedAt: data.exportedAt || new Date().toISOString(),
      providers: [],
      sessions: [],
      templates: [],
    };
    
    // 清理供应商数据
    if (data.providers && Array.isArray(data.providers)) {
      cleanedData.providers = data.providers
        .filter(provider => this.isValidProvider(provider))
        .map(provider => this.cleanProvider(provider));
    }
    
    // 清理会话数据
    if (data.sessions && Array.isArray(data.sessions)) {
      cleanedData.sessions = data.sessions
        .filter(session => this.isValidSession(session))
        .map(session => this.cleanSession(session));
    }
    
    // 清理模板数据
    if (data.templates && Array.isArray(data.templates)) {
      cleanedData.templates = data.templates
        .filter(template => this.isValidTemplate(template))
        .map(template => this.cleanTemplate(template));
    }
    
    return cleanedData;
  }
  
  /**
   * 验证供应商数据
   */
  private static isValidProvider(provider: any): provider is ApiProvider {
    if (!provider || typeof provider !== 'object') return false;
    
    // 必需字段
    if (!provider.id || typeof provider.id !== 'string') return false;
    if (!provider.name || typeof provider.name !== 'string') return false;
    if (!provider.baseUrl || typeof provider.baseUrl !== 'string') return false;
    if (!provider.providerType || typeof provider.providerType !== 'string') return false;
    
    // 可选字段验证
    if (provider.apiKey && typeof provider.apiKey !== 'string') return false;
    if (provider.models && !Array.isArray(provider.models)) return false;
    if (provider.config && typeof provider.config !== 'object') return false;
    
    return true;
  }
  
  /**
   * 清理供应商数据
   */
  private static cleanProvider(provider: ApiProvider): ApiProvider {
    return {
      id: provider.id.trim(),
      name: provider.name.trim(),
      baseUrl: provider.baseUrl.trim(),
      apiKey: provider.apiKey ? provider.apiKey.trim() : '',
      providerType: provider.providerType,
      models: Array.isArray(provider.models) 
        ? provider.models.map(model => model.toString().trim())
        : [],
      defaultModel: provider.defaultModel ? provider.defaultModel.toString().trim() : undefined,
      config: provider.config || {},
      createdAt: provider.createdAt || new Date().toISOString(),
      updatedAt: provider.updatedAt || new Date().toISOString(),
    };
  }
  
  /**
   * 验证会话数据
   */
  private static isValidSession(session: any): session is ChatSession {
    if (!session || typeof session !== 'object') return false;
    
    // 必需字段
    if (!session.id || typeof session.id !== 'string') return false;
    if (!session.name || typeof session.name !== 'string') return false;
    if (!session.providerId || typeof session.providerId !== 'string') return false;
    if (!session.model || typeof session.model !== 'string') return false;
    if (!session.messages || !Array.isArray(session.messages)) return false;
    
    // 验证消息
    for (const message of session.messages) {
      if (!message || typeof message !== 'object') return false;
      if (!message.id || typeof message.id !== 'string') return false;
      if (!message.role || !['user', 'assistant', 'system'].includes(message.role)) return false;
      if (!message.content || typeof message.content !== 'string') return false;
      if (!message.timestamp || typeof message.timestamp !== 'string') return false;
    }
    
    return true;
  }
  
  /**
   * 清理会话数据
   */
  private static cleanSession(session: ChatSession): ChatSession {
    // 清理消息
    const cleanedMessages = session.messages.map(message => ({
      id: message.id.trim(),
      role: message.role as 'user' | 'assistant' | 'system',
      content: message.content.trim(),
      timestamp: message.timestamp || new Date().toISOString(),
      providerId: message.providerId ? message.providerId.trim() : undefined,
      model: message.model ? message.model.trim() : undefined,
      tokens: typeof message.tokens === 'number' ? message.tokens : undefined,
    }));
    
    // 计算统计信息
    const messageCount = cleanedMessages.length;
    const tokenCount = cleanedMessages.reduce((sum, msg) => sum + (msg.tokens || 0), 0);
    const lastMessageAt = cleanedMessages.length > 0 
      ? cleanedMessages[cleanedMessages.length - 1].timestamp 
      : new Date().toISOString();
    
    return {
      id: session.id.trim(),
      name: session.name.trim(),
      providerId: session.providerId.trim(),
      model: session.model.trim(),
      messages: cleanedMessages,
      config: session.config || {
        temperature: 0.7,
        maxTokens: 1000,
        topP: 1.0,
        frequencyPenalty: 0,
        presencePenalty: 0,
        stream: false,
      },
      tags: Array.isArray(session.tags) 
        ? session.tags.map(tag => tag.toString().trim()).filter(tag => tag)
        : [],
      summary: session.summary ? session.summary.trim() : undefined,
      messageCount,
      tokenCount,
      lastMessageAt,
      createdAt: session.createdAt || new Date().toISOString(),
      updatedAt: session.updatedAt || new Date().toISOString(),
    };
  }
  
  /**
   * 验证模板数据
   */
  private static isValidTemplate(template: any): template is ChatTemplate {
    if (!template || typeof template !== 'object') return false;
    
    if (!template.id || typeof template.id !== 'string') return false;
    if (!template.name || typeof template.name !== 'string') return false;
    if (!template.description || typeof template.description !== 'string') return false;
    if (!template.content || typeof template.content !== 'string') return false;
    if (!template.category || typeof template.category !== 'string') return false;
    if (!template.tags || !Array.isArray(template.tags)) return false;
    
    return true;
  }
  
  /**
   * 清理模板数据
   */
  private static cleanTemplate(template: ChatTemplate): ChatTemplate {
    return {
      id: template.id.trim(),
      name: template.name.trim(),
      description: template.description.trim(),
      content: template.content.trim(),
      category: template.category as any,
      tags: template.tags.map(tag => tag.toString().trim()),
    };
  }
  
  /**
   * 分析导入数据
   */
  static analyzeImportData(data: DataExport): {
    providers: { count: number; issues: string[] };
    sessions: { count: number; issues: string[] };
    templates: { count: number; issues: string[] };
    totalSize: string;
    estimatedImportTime: string;
  } {
    const analysis: {
      providers: { count: number; issues: string[] };
      sessions: { count: number; issues: string[] };
      templates: { count: number; issues: string[] };
      totalSize: string;
      estimatedImportTime: string;
    } = {
      providers: { count: 0, issues: [] },
      sessions: { count: 0, issues: [] },
      templates: { count: 0, issues: [] },
      totalSize: '0 KB',
      estimatedImportTime: '< 1秒',
    };
    
    // 统计数量
    if (data.providers) {
      analysis.providers.count = data.providers.length;
      const invalidProviders = data.providers.filter((p: any) => !this.isValidProvider(p));
      if (invalidProviders.length > 0) {
        analysis.providers.issues.push(`${invalidProviders.length} 个供应商数据格式无效`);
      }
    }
    
    if (data.sessions) {
      analysis.sessions.count = data.sessions.length;
      const invalidSessions = data.sessions.filter((s: any) => !this.isValidSession(s));
      if (invalidSessions.length > 0) {
        analysis.sessions.issues.push(`${invalidSessions.length} 个会话数据格式无效`);
      }
      
      // 计算总消息数
      const totalMessages = data.sessions.reduce((sum: number, session: any) => sum + (session.messages?.length || 0), 0);
      if (totalMessages > 1000) {
        analysis.sessions.issues.push(`包含大量消息（${totalMessages} 条），导入可能需要较长时间`);
      }
    }
    
    if (data.templates) {
      analysis.templates.count = data.templates.length;
      const invalidTemplates = data.templates.filter((t: any) => !this.isValidTemplate(t));
      if (invalidTemplates.length > 0) {
        analysis.templates.issues.push(`${invalidTemplates.length} 个模板数据格式无效`);
      }
    }
    
    // 计算文件大小
    const jsonString = JSON.stringify(data);
    const sizeInBytes = new Blob([jsonString]).size;
    analysis.totalSize = this.formatFileSize(sizeInBytes);
    
    // 估算导入时间
    const totalItems = analysis.providers.count + analysis.sessions.count + analysis.templates.count;
    if (totalItems > 1000) {
      analysis.estimatedImportTime = '10-30秒';
    } else if (totalItems > 100) {
      analysis.estimatedImportTime = '3-10秒';
    } else if (totalItems > 10) {
      analysis.estimatedImportTime = '1-3秒';
    }
    
    return analysis;
  }
  
  /**
   * 格式化文件大小
   */
  private static formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
  
  /**
   * 生成导入报告
   */
  static generateImportReport(
    data: DataExport,
    importResult: ImportResult
  ): string {
    const analysis = this.analyzeImportData(data);
    const importTime = new Date().toLocaleString('zh-CN');
    
    const report = `
# API 对话数据导入报告

## 导入信息
- 导入时间: ${importTime}
- 数据版本: ${data.version}
- 原始导出时间: ${data.exportedAt}

## 数据统计
- 供应商数量: ${analysis.providers.count}
- 会话数量: ${analysis.sessions.count}
- 模板数量: ${analysis.templates.count}
- 文件大小: ${analysis.totalSize}

## 导入结果
- 导入状态: ${importResult.success ? '成功' : '失败'}
- 导入供应商: ${importResult.importedCount.providers}
- 导入会话: ${importResult.importedCount.sessions}
- 导入模板: ${importResult.importedCount.templates}

## 问题和警告
${importResult.errors.length > 0 ? `
### 错误信息
${importResult.errors.map(error => `- ❌ ${error}`).join('\n')}
` : ''}

${importResult.warnings.length > 0 ? `
### 警告信息
${importResult.warnings.map(warning => `- ⚠️ ${warning}`).join('\n')}
` : ''}

${analysis.providers.issues.length > 0 || analysis.sessions.issues.length > 0 || analysis.templates.issues.length > 0 ? `
### 数据问题
${[...analysis.providers.issues, ...analysis.sessions.issues, ...analysis.templates.issues]
  .map(issue => `- ⚠️ ${issue}`)
  .join('\n')}
` : ''}

## 导入说明
1. 导入操作已${importResult.success ? '成功' : '失败'}完成
2. 系统已自动跳过重复的数据项
3. 建议验证导入数据的完整性
4. 如有问题，请检查原始数据格式

## 后续建议
1. 验证导入的供应商配置是否正确
2. 检查导入的会话数据是否完整
3. 测试导入的模板是否可用
4. 定期备份当前数据

---
*报告生成时间: ${importTime}*
    `.trim();
    
    return report;
  }
  
  /**
   * 比较导入前后的数据变化
   */
  static compareDataChanges(
    existingProviders: ApiProvider[],
    existingSessions: ChatSession[],
    existingTemplates: ChatTemplate[],
    importedData: DataExport
  ): {
    newProviders: ApiProvider[];
    newSessions: ChatSession[];
    newTemplates: ChatTemplate[];
    duplicateProviders: ApiProvider[];
    duplicateSessions: ChatSession[];
    duplicateTemplates: ChatTemplate[];
  } {
    const existingProviderIds = new Set(existingProviders.map(p => p.id));
    const existingSessionIds = new Set(existingSessions.map(s => s.id));
    const existingTemplateIds = new Set(existingTemplates.map(t => t.id));
    
    const newProviders = (importedData.providers || []).filter(p => !existingProviderIds.has(p.id));
    const newSessions = (importedData.sessions || []).filter(s => !existingSessionIds.has(s.id));
    const newTemplates = (importedData.templates || []).filter(t => !existingTemplateIds.has(t.id));
    
    const duplicateProviders = (importedData.providers || []).filter(p => existingProviderIds.has(p.id));
    const duplicateSessions = (importedData.sessions || []).filter(s => existingSessionIds.has(s.id));
    const duplicateTemplates = (importedData.templates || []).filter(t => existingTemplateIds.has(t.id));
    
    return {
      newProviders,
      newSessions,
      newTemplates,
      duplicateProviders,
      duplicateSessions,
      duplicateTemplates,
    };
  }
}